import { useEffect, useState, useMemo } from "react";
import { MdAdd, MdDelete, MdEdit, MdEvent, MdSearch, MdChevronLeft, MdChevronRight } from "react-icons/md";
import Card from "components/card";
import { api } from "api";

const CATEGORIES = ["Tumu", "KDV", "SGK", "Gelir", "Kurumlar", "Muhtasar", "Damga", "Gecici Vergi", "BA-BS", "Diger"];
const CAT_COLORS: Record<string, string> = {
  KDV: "bg-blue-100 text-blue-700", SGK: "bg-green-100 text-green-700", Gelir: "bg-purple-100 text-purple-700",
  Kurumlar: "bg-orange-100 text-orange-700", Muhtasar: "bg-pink-100 text-pink-700", Damga: "bg-yellow-100 text-yellow-700",
  "Gecici Vergi": "bg-indigo-100 text-indigo-700", "BA-BS": "bg-teal-100 text-teal-700", Diger: "bg-gray-100 text-gray-600",
};
const MONTHS = ["", "Ocak", "Subat", "Mart", "Nisan", "Mayis", "Haziran", "Temmuz", "Agustos", "Eylul", "Ekim", "Kasim", "Aralik"];
const PER_PAGE = 20;

const VergiTakvimi = () => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ title: "", description: "", due_date: "", category: "KDV", reminder_days: 3 });
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [catFilter, setCatFilter] = useState("Tumu");
  const [monthFilter, setMonthFilter] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    api.getVergiTakvimi().then(setItems).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    let list = [...items];
    if (catFilter !== "Tumu") list = list.filter(i => i.category === catFilter);
    if (monthFilter > 0) list = list.filter(i => new Date(i.due_date).getMonth() + 1 === monthFilter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(i => i.title.toLowerCase().includes(s) || (i.description || "").toLowerCase().includes(s));
    }
    return list;
  }, [items, catFilter, monthFilter, search]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  useEffect(() => { setPage(1); }, [catFilter, monthFilter, search]);

  const openCreate = () => {
    setEditId(null);
    setForm({ title: "", description: "", due_date: "", category: "KDV", reminder_days: 3 });
    setError("");
    setShowForm(true);
  };

  const openEdit = (item: any) => {
    setEditId(item.id);
    setForm({ title: item.title, description: item.description || "", due_date: item.due_date, category: item.category, reminder_days: item.reminder_days });
    setError("");
    setShowForm(true);
  };

  const handleSave = async () => {
    setError("");
    if (!form.title || !form.due_date) { setError("Baslik ve tarih zorunludur"); return; }
    try {
      if (editId) {
        await api.updateVergi(editId, form);
        setMsg("Guncellendi!");
      } else {
        await api.createVergi(form);
        setMsg("Eklendi!");
      }
      setShowForm(false);
      load();
      setTimeout(() => setMsg(""), 3000);
    } catch (e: any) { setError(e.message); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Bu kaydi silmek istediginize emin misiniz?")) return;
    await api.deleteVergi(id);
    load();
  };

  const isPast = (d: string) => new Date(d) < new Date();

  return (
    <div>
      {msg && <div className="mb-4 rounded-xl bg-green-50 p-3 text-sm text-green-700">{msg}</div>}

      <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h4 className="text-lg font-bold text-navy-700 dark:text-white">Vergi Takvimi ({filtered.length}/{items.length})</h4>
        <button onClick={openCreate} className="flex items-center gap-2 rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600 transition">
          <MdAdd className="h-5 w-5" /> Yeni Tarih Ekle
        </button>
      </div>

      {/* Filters */}
      <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <MdSearch className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Ara..." className="w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-4 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
        </div>
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
          className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white">
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={monthFilter} onChange={(e) => setMonthFilter(Number(e.target.value))}
          className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white">
          <option value={0}>Tum Aylar</option>
          {MONTHS.slice(1).map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
      </div>

      <Card extra="mt-4 p-4">
        {loading ? (
          <p className="p-4 text-center text-gray-500">Yukleniyor...</p>
        ) : paged.length === 0 ? (
          <p className="p-4 text-center text-gray-500">Kayit bulunamadi</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-navy-700">
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Tarih</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Baslik</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Kategori</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-gray-600">Islem</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((item) => (
                    <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-700/50 transition">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <MdEvent className={`h-5 w-5 ${isPast(item.due_date) ? "text-red-400" : "text-brand-500"}`} />
                          <span className={`text-sm font-medium ${isPast(item.due_date) ? "text-red-500" : "text-navy-700 dark:text-white"}`}>
                            {new Date(item.due_date).toLocaleDateString("tr-TR")}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-navy-700 dark:text-white">{item.title}</p>
                        {item.description && item.description !== item.title && (
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{item.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-3 py-1 text-xs font-medium ${CAT_COLORS[item.category] || CAT_COLORS.Diger}`}>{item.category}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => openEdit(item)} className="text-brand-500 hover:text-brand-700"><MdEdit className="h-5 w-5" /></button>
                          <button onClick={() => handleDelete(item.id)} className="text-red-400 hover:text-red-600"><MdDelete className="h-5 w-5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-gray-500">Sayfa {page}/{totalPages}</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="rounded-lg border border-gray-200 p-2 hover:bg-gray-50 disabled:opacity-30 dark:border-navy-600">
                    <MdChevronLeft className="h-5 w-5" />
                  </button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="rounded-lg border border-gray-200 p-2 hover:bg-gray-50 disabled:opacity-30 dark:border-navy-600">
                    <MdChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-navy-800" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-4">{editId ? "Tarih Duzenle" : "Yeni Tarih Ekle"}</h3>
            {error && <div className="mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Baslik *</label>
                <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Orn: KDV Beyannamesi" className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Aciklama</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Detay bilgi" rows={2} className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Son Tarih *</label>
                <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Kategori</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white">
                  {CATEGORIES.filter(c => c !== "Tumu").map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Hatirlatma (gun once)</label>
                <input type="number" min={0} max={30} value={form.reminder_days} onChange={(e) => setForm({ ...form, reminder_days: Number(e.target.value) })}
                  className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white" />
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setShowForm(false)} className="rounded-xl px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100">Iptal</button>
              <button onClick={handleSave} className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600">{editId ? "Guncelle" : "Ekle"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VergiTakvimi;
